import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { joinBy } from '@pbk/utils';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useMemo, useState } from 'react';
import { styled } from 'styletron-react';
import { staticColors } from '../../../../const/colors/staticColors';
import { zIndexes } from '../../../../const/zIndexes';
import { useFloatingSurfaceStyle } from '../../../../hooks/useFloatingSurfaceStyle';
import { useTestIdAttribute } from '../../../../hooks/useTestIdAttribute';
import { HorizontalDivider } from '../../../HorizontalDivider/HorizontalDivider';
import { Icon } from '../../../Icon/Icon';
import { Text } from '../../../Text/Text';
import { textVariantStyleMap } from '../../../Text/textConst';
import { Box } from '../../../layout/Box';
import { FlexContainer } from '../../../layout/FlexContainer';
import { InputBase } from '../../InputBase/InputBase';
import { INPUT_TEXT_VARIANT, useInputStyle } from '../../useInputStyles';
import { useIsErrorTestAttribute } from '../../useIsErrorTestAttribute';
const StyledContent = styled(RadixDropdownMenu.Content, (p) => ({
    ...p.$floatingSurfaceStyle,
    minWidth: 'var(--radix-dropdown-menu-trigger-width)',
    zIndex: zIndexes.select,
}));
const StyledTrigger = styled(RadixDropdownMenu.Trigger, (p) => ({
    ...p.$style,
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
    ':hover': {
        cursor: 'pointer',
    },
    ':disabled': {
        cursor: 'not-allowed',
    },
}));
const StyledCheckboxItem = styled(RadixDropdownMenu.CheckboxItem, ({ $isActive }) => ({
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: $isActive
        ? staticColors.selectItemActiveBackgroundColor
        : undefined,
    ':focus-visible': {
        outline: 'none',
    },
    ':hover': {
        outline: 'none',
        backgroundColor: $isActive
            ? staticColors.multiSelectItemActiveHoverBackgroundColor
            : staticColors.multiSelectItemDisabledHoverBackgroundColor,
    },
}));
const StyledValueContainer = styled('div', (p) => ({
    ...textVariantStyleMap[INPUT_TEXT_VARIANT],
    paddingTop: p.$paddingTop,
    paddingBottom: p.$paddingBottom,
    height: p.$height,
    minWidth: 0,
    overflow: 'hidden',
    textWrap: 'nowrap',
    color: p.$hasNoSelectedOptions ? staticColors.inputPlaceholder : undefined,
}));
export const MultiSelect = ({ options, withIndicator = true, value = [], onValueChange, placeholder, selectedPlaceholder, label, caption, tooltip, tooltipTabIndex, error = false, updated = false, isRequiredLabel = false, gapRight = false, testId, readOnly = false, disabled, }) => {
    const [open, setOpen] = useState(false);
    const floatingSurfaceStyle = useFloatingSurfaceStyle();
    const { paddingTop: inputPaddingTop, paddingBottom: inputPaddingBottom, outline: _outline, ...inputStyle } = useInputStyle({
        isFocused: false,
        readOnly,
    });
    const isErrorTestAttribute = useIsErrorTestAttribute(!!error);
    const testIdAttribute = useTestIdAttribute(testId);
    const stringifiedLineHeight = inputStyle.lineHeight.toString();
    const isDisabled = disabled || readOnly;
    const selectedOptions = useMemo(() => {
        return options.filter((option) => value.includes(option.value));
    }, [options, value]);
    const hasNoSelectedOptions = selectedOptions.length === 0;
    const displayValue = useMemo(() => {
        if (hasNoSelectedOptions) {
            return placeholder;
        }
        if (selectedPlaceholder) {
            return selectedPlaceholder;
        }
        // Join selected option texts with commas
        const selectedTexts = selectedOptions.map((option) => option.text);
        return joinBy(selectedTexts, ', ');
    }, [hasNoSelectedOptions, selectedPlaceholder, selectedOptions, placeholder]);
    const handleValueChange = useCallback((optionValue, checked) => {
        if (!onValueChange)
            return;
        const newValues = checked
            ? [...value, optionValue]
            : value.filter((v) => v !== optionValue);
        onValueChange(newValues);
    }, [value, onValueChange]);
    const maybeIcon = !readOnly ? _jsx(Icon, { name: 'KeyboardArrowDown' }) : null;
    return (_jsxs(RadixDropdownMenu.Root, { open: open, onOpenChange: setOpen, children: [_jsx(InputBase, { label: label, isRequiredLabel: isRequiredLabel, caption: caption, tooltip: tooltip, tooltipTabIndex: tooltipTabIndex, error: error, updated: updated, isFocused: false, gapRight: gapRight, testId: testId, children: _jsx(StyledTrigger, { $style: inputStyle, disabled: isDisabled, ...testIdAttribute, ...isErrorTestAttribute, children: _jsxs(FlexContainer, { horizontalAlign: 'spaceBetween', verticalAlign: 'center', children: [_jsx(StyledValueContainer, { $paddingTop: inputPaddingTop, $paddingBottom: inputPaddingBottom, $height: stringifiedLineHeight, $hasNoSelectedOptions: hasNoSelectedOptions, children: displayValue }), maybeIcon] }) }) }), _jsx(RadixDropdownMenu.Portal, { children: _jsx(StyledContent, { $floatingSurfaceStyle: floatingSurfaceStyle, side: 'bottom', align: 'start', children: options.map((option, index) => {
                        const isLast = index === options.length - 1;
                        const isSelected = value.includes(option.value);
                        const handleCheckedChange = (checked) => {
                            handleValueChange(option.value, checked);
                        };
                        const handleSelect = (event) => {
                            event.preventDefault(); // Prevent dropdown from closing
                        };
                        const hasIcon = withIndicator && isSelected;
                        const maybeCheckIcon = hasIcon && (_jsx(Icon, { name: 'Check', size: 'small', color: 'action' }));
                        const maybeDivider = !isLast && _jsx(HorizontalDivider, {});
                        return (_jsx(StyledCheckboxItem, { $isActive: isSelected, checked: isSelected, onCheckedChange: handleCheckedChange, onSelect: handleSelect, children: _jsxs(Box, { spacingHorizontal: true, spacingTop: true, children: [_jsxs(FlexContainer, { direction: 'row', verticalAlign: 'center', horizontalAlign: 'spaceBetween', children: [_jsx(Text, { variant: INPUT_TEXT_VARIANT, align: 'start', overflow: 'hidden', textOverflow: 'ellipsis', children: option.text }), maybeCheckIcon] }), _jsx(Box, { spacingTop: true }), maybeDivider] }) }, option.value));
                    }) }) })] }));
};
